import {useState, type FormEvent, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {InviteLink} from '@/components/invite-link'
import {
  PersonaIdentityFields,
  clearChangedIdentityErrors,
  validateIdentity,
  type PersonaIdentity,
} from '@/components/persona-identity-fields'

/** `inviteToken` is null when the person already had a password. */
export type RosterCreateResult = {inviteToken: string | null; extra?: ReactNode}

export function RosterCreateSuccess({
  description,
  extra,
  inviteToken,
  onClose,
}: {
  description: string
  extra?: ReactNode
  inviteToken: string | null
  onClose: () => void
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{description}</p>
      {extra}
      {inviteToken && <InviteLink token={inviteToken} kind="invite" />}
      <div className="flex justify-end">
        <Button type="button" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  )
}

export function RosterCreateDialog({
  open,
  onClose,
  tKey,
  testPrefix,
  result,
  error,
  pending,
  identity,
  onIdentityChange,
  onSubmit,
  onReset,
}: {
  open: boolean
  onClose: () => void
  tKey: string
  testPrefix: string
  result: RosterCreateResult | null
  error: string | null
  pending: boolean
  identity: PersonaIdentity
  onIdentityChange: (next: PersonaIdentity) => void
  onSubmit: () => void
  onReset: () => void
}) {
  const {t} = useTranslation()
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof PersonaIdentity, string>>>({})

  function handleOpenChange(next: boolean) {
    if (!next) {
      setFieldErrors({})
      onReset()
      onClose()
    }
  }

  function handleIdentityChange(next: PersonaIdentity) {
    onIdentityChange(next)
    setFieldErrors((prev) => clearChangedIdentityErrors(prev, next, identity))
  }

  function blurField(field: keyof PersonaIdentity) {
    setFieldErrors((prev) => ({...prev, [field]: validateIdentity(identity)[field]}))
  }

  // Named `validate*`: the R4 gate only sees a direct `@/lib/validation` import or a local
  // `validate*` function, not a re-exported validator.
  function validateAndSubmit(e: FormEvent) {
    e.preventDefault()
    const errors = validateIdentity(identity)
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    onSubmit()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid={`${testPrefix}-create-dialog`}>
        <DialogHeader>
          <DialogTitle>{t(`${tKey}.title`)}</DialogTitle>
        </DialogHeader>
        {result !== null ? (
          <RosterCreateSuccess
            description={t(`${tKey}.description`)}
            extra={result.extra}
            inviteToken={result.inviteToken}
            onClose={() => handleOpenChange(false)}
          />
        ) : (
          <form onSubmit={validateAndSubmit} noValidate className="space-y-4">
            <p className="text-sm text-muted-foreground">{t(`${tKey}.description`)}</p>
            <div className="flex flex-col gap-4">
              <PersonaIdentityFields
                value={identity}
                onChange={handleIdentityChange}
                tKey={tKey}
                testPrefix={testPrefix}
                errors={fieldErrors}
                onBlurField={blurField}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" data-testid={`${testPrefix}-create-submit`} disabled={pending}>
                {pending ? t(`${tKey}.creating`) : t(`${tKey}.submit`)}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
